import { Chip } from "@mui/material";
import { ReactNode, useCallback, useMemo } from "react";

import { ExtractedRecord, ReimbursementRowForecast } from "@/types";

import { renderLineItemsExpand } from "../components/document-review/reimbursementReviewHelpers";

import { expandableRowKey } from "../utils/recordIndexing";

/**
 * Schema-specific rendering for REIMBURSEMENT rows in the document-review grid.
 *
 * `DocumentReviewTab` orchestrates three render concerns — `renderExpandRow`,
 * `renderExpandLabel`, and the `expandableRowSet` precompute — that each
 * previously had REIMBURSEMENT-specific branches mashed inline alongside the
 * topSchema (Claimants tab) branches. As the surface accumulated more
 * schema-specific behavior (line items, badge counts, survivor filtering),
 * those branches kept growing in three places.
 *
 * Pattern: bundle each schema's special-case rendering into its own hook so
 * `DocumentReviewTab` stays a coordinator. The component still owns the
 * schema-routing decision (which renderer to invoke per `currentSchema`) but
 * not the body of each renderer. When the next schema needs special-case
 * rendering, mirror this hook's shape rather than re-mashing branches into
 * the component.
 */
export interface ReimbursementSchemaRendering {
  /** Records eligible for the expand chevron — those whose forecast carries line items. */
  /**
   * Keyed by expandableRowKey (absolute record_index, or the object when it
   * has none) — NOT by object identity.
   *
   * Must match how DocumentReviewTab looks rows up, and that is enforced only
   * by both sides calling the same helper. When this was Set<ExtractedRecord>
   * and the caller switched to index keying, the union type absorbed the
   * mismatch and every reimbursement chevron would have silently disappeared
   * (!4682 review, caught before it shipped).
   */
  expandableRowSet: Set<number | ExtractedRecord>;
  /** Per-row expand panel content — the line-items table or null. */
  renderExpandRow: (record: ExtractedRecord) => ReactNode;
  /** Per-row expand chevron badge — line-item count chip or null. */
  renderExpandLabel: (record: ExtractedRecord) => ReactNode;
}

interface UseReimbursementSchemaRenderingArgs {
  /**
   * Reimbursement-schema records the grid will render. Pass the bucketed
   * `recordsBySchema[REIMBURSEMENT]` array; safe to pass an empty array when
   * the current schema is something else (the hook's outputs are then
   * trivially empty / no-op).
   */
  records: ExtractedRecord[];
  getReimbursementRowForRecord: (record: ExtractedRecord) => ReimbursementRowForecast | undefined;
}

export const useReimbursementSchemaRendering = ({
  records,
  getReimbursementRowForRecord,
}: UseReimbursementSchemaRenderingArgs): ReimbursementSchemaRendering => {
  // Suppress the expand chevron for rows whose panel would be empty.
  // Expandability for REIMBURSEMENT is "row has line_items" — the projection
  // attaches per-line claim records to the parent reimbursement, and the
  // chevron only earns its place when at least one is present.
  const expandableRowSet = useMemo(() => {
    const set = new Set<number | ExtractedRecord>();
    for (const r of records) {
      const forecast = getReimbursementRowForRecord(r);
      if (forecast?.line_items && forecast.line_items.length > 0) set.add(expandableRowKey(r));
    }
    return set;
  }, [records, getReimbursementRowForRecord]);

  // Expand reveals the parent's per-line claim items as a small read-only
  // table. Sourced from the projection's positively-associated line_items,
  // not from raw extracted_records — so the visible records here have
  // already been filtered + deduped, but the per-line context is surfaced.
  const renderExpandRow = useCallback(
    (record: ExtractedRecord): ReactNode => renderLineItemsExpand(getReimbursementRowForRecord(record)),
    [getReimbursementRowForRecord]
  );

  // Badge count = number of line items on the parent's forecast. Suppressed
  // when zero so the chevron doesn't render an empty pill.
  const renderExpandLabel = useCallback(
    (record: ExtractedRecord): ReactNode => {
      const forecast = getReimbursementRowForRecord(record);
      const count = forecast?.line_items?.length ?? 0;
      if (count === 0) return null;
      return (
        <Chip label={count} size="small" sx={{ height: 18, fontSize: "0.7rem", "& .MuiChip-label": { px: 0.75 } }} />
      );
    },
    [getReimbursementRowForRecord]
  );

  return { expandableRowSet, renderExpandRow, renderExpandLabel };
};
